import {ActionTree} from 'vuex'
import axios, {AxiosResponse, AxiosError} from "axios";
import * as moment from 'moment';
import SubPanel from '../ts/SubPanel';

export const actions: ActionTree<any, any> = {

    loadSubPanels: ({state, getters, commit, dispatch}, force) => {

        if (force) state.expires = null;

        return new Promise((resolve, reject) => {

            // Pull from store if loaded and cache hasn't expired
            if (state.expires && moment().isBefore(state.expires)) {
                resolve(state.subPanels);

            } else {

                commit('setLoading', true);

                axios.get('/sub-panels')

                    .then(({data}) => {
                        commit('setData', data.subPanels);
                        commit('setLoading', false);
                        resolve(data.subPanels);
                    })
                    .catch(error => {
                        commit('setLoading', false);
                        reject(error.response);
                    });
            }
        });
    },

    updateSubPanel: ({state, getters, commit, dispatch}, subPanel: SubPanel) => {

        commit('setSaving', true);

        const backup = state.subPanels.find((sp: SubPanel) => sp.id === subPanel.id);

        return new Promise((resolve, reject) => {

            commit('update', subPanel);

            axios.post('/sub-panel', subPanel)

                .then(response => {

                    commit('setSaving', false);
                    resolve(response);
                })
                .catch(error => {
                    commit('update', backup);
                    commit('setSaving', false);
                    reject(error.response);
                });
        });
    },

    createSubPanel: ({state, getters, commit, dispatch}, subPanel: SubPanel) => {

        commit('setSaving', true);

        return new Promise((resolve, reject) => {

            axios.put('/sub-panel', subPanel)

                .then((response) => {
                    commit('create', response.data.subPanel);
                    commit('setSaving', false);
                    resolve(response);
                })
                .catch((error) => {
                    commit('setSaving', false);
                    reject(error.response);
                });
        });
    },

    deleteSubPanel: ({state, getters, commit, dispatch}, id: number) => {

        const backup = [...state.subPanels];

        return new Promise((resolve, reject) => {

            commit('delete', id);

            axios.delete('/sub-panel/' + id)

                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    commit('setData', backup);
                    reject(error.response);
                });
        });
    },

    updateLogo: ({state, getters, commit, dispatch}, formData) => {

        return new Promise((resolve, reject) => {

            axios.post('/sub-panel/' + formData.get('id') + '/logo', formData)

                .then((response) => {
                    commit('update', response.data.subPanel);
                    resolve(response);
                })
                .catch((error) => {
                    reject(error.response);
                });
        });
    },

    /**
     * Used by the landing page editor
     *
     * @param state
     * @param getters
     * @param commit
     * @param dispatch
     * @param id
     * @param locale
     */
    loadLandingPages: ({state, getters, commit, dispatch}, {id, locale}) => {

        return new Promise((resolve, reject) => {

            axios.get('/sub-panel/' + id + '/landing-pages/' + locale)

                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error.response);
                });
        });
    },

    /**
     * User by the email template editor
     *
     * @param state
     * @param getters
     * @param commit
     * @param dispatch
     * @param id
     * @param locale
     */
    loadEmailTemplates: ({state, getters, commit, dispatch}, {id, locale}) => {

        return new Promise((resolve, reject) => {

            axios.get('/sub-panel/' + id + '/email-templates/' + locale)

                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error.response);
                });
        });
    },


    /**
     * Used by the projects to load specific templates for sub panels use in the query
     *
     * @param state
     * @param getters
     * @param commit
     * @param dispatch
     * @param type
     * @param subPanelIds
     */
    loadAllEmailTemplates: ({state, getters, commit, dispatch}, {type, subPanelIds}) => {

        return new Promise((resolve, reject) => {

            axios.get('/email-templates/' + type + '?sub_panels=' + subPanelIds.join(','))

                .then((response) => {

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    loadAllReminderTemplates: ({state, getters, commit, dispatch}, {type, subPanelIds}) => {

        return new Promise((resolve, reject) => {

            axios.get('/email-templates/' + type + '?sub_panels=' + subPanelIds.join(','))

                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });

        });
    },

    /**
     * Used by the project to load specific templates for sub panels used in the query
     *
     * @param state
     * @param getters
     * @param commit
     * @param dispatch
     * @param type
     * @param subPanelIds
     */
    loadAllLandingPageTemplates: ({state, getters, commit, dispatch}, {type, subPanelIds}) => {

        return new Promise((resolve, reject) => {

                axios.get('/landing-page-templates/' + type + '?sub_panels=' + subPanelIds.join(','))

                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error.response);
                    });
        });
    },

    saveLandingPageSettings: ({state, getters, commit, dispatch}, {subPanel, settings}) => {

        return new Promise((resolve, reject) => {

            axios.post('/sub-panel/' + subPanel.id + '/landing-pages/settings', settings)

                .then((response) => {

                    // Replace the original object with a new object / with applied settings
                    const index = state.subPanels.findIndex((target: SubPanel) => subPanel.id === target.id);

                    subPanel.landing_page_settings = Object.assign({}, settings);

                    state.subPanels.splice(index, 1, subPanel);

                    resolve(response);
                })
                .catch((error) => {
                    reject(error.response);
                });
        });
    },

    saveLandingPageTranslations: ({state, getters, commit, dispatch}, {id, locale, data}) => {

        return new Promise((resolve, reject) => {

            axios.post('/sub-panel/' + id + '/landing-pages/' + locale, data)

                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error.response);
                });
        });
    },

    saveEmailSettings: ({state, getters, commit, dispatch}, {subPanel, settings}) => {

        return new Promise((resolve, reject) => {

            axios.post('/sub-panel/' + subPanel.id + '/email-templates/settings', settings)

                .then((response) => {

                    // Replace the original object with a new object / with applied settings
                    const index = state.subPanels.findIndex((target: SubPanel) => subPanel.id === target.id);

                    subPanel.email_settings = Object.assign({}, settings);

                    state.subPanels.splice(index, 1, subPanel);

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    saveEmailTranslations: ({state, getters, commit, dispatch}, {id, locale, data}) => {

        return new Promise((resolve, reject) => {

            axios.post('/sub-panel/' + id + '/email-templates/' + locale, data)

                .then((response) => {

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    loadSubPanelTranslations: ({state, getters, commit, dispatch}, subPanelId) => {

        return new Promise((resolve, reject) => {

            axios.get('/sub-panel/' + subPanelId + '/localizations')
                .then(response => {
                    commit('setSubPanelTranslations', response.data.translations);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        });
    },

    saveSubPanelTranslations: ({state, getters, commit, dispatch}, {id, data}) => {

        return new Promise((resolve, reject) => {

            axios.post('/sub-panel/' + id + '/localizations', data)

                .then((response) => {

                    resolve(response);
                })
                .catch((error) => {
                    reject(error.response);
                });
        });
    },

    getApiClients: ({state, getters, commit, dispatch}, id: Number) => {

        return new Promise((resolve, reject) => {

            axios.get('/sub-panel/' + id + '/api-clients')
                .then(response => {
                    commit('setApiClients', response.data.clients);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        });
    },

    createApiClient: ({state, getters, commit, dispatch}, {id, payload}) => {

        return new Promise((resolve, reject) => {

            axios.put('/sub-panel/' + id + '/api-clients', payload)
                .then(response => {
                    commit('addApiClient', response.data.client);
                    resolve(response)
                })
                .catch(error => {
                    reject(error);
                })
        });
    },

    deleteApiClient: ({state, getters, commit, dispatch}, {id, clientId}) => {

        const apiClients = [...state.apiClients];

        commit('deleteApiClient', id);

        return new Promise((resolve, reject) => {

            axios.delete('/sub-panel/' + id + '/api-clients/' + clientId)
                .then(response => {

                    resolve(response)
                })
                .catch(error => {
                    commit('setApiClients', apiClients);
                    reject(error);
                })

        });
    },

    loadRegistrationSurvey: ({state, getters, commit, dispatch}, {id}) => {

        return new Promise((resolve, reject) => {

            axios.get('/sub-panel/' + id + '/registration-survey')

                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    saveRegistrationSurvey: ({state, getters, commit, dispatch}, {id, data}) => {

        return new Promise((resolve, reject) => {

            axios.post('/sub-panel/' + id + '/registration-survey', data)

                .then((response) => {
                    commit('enableRegistrationSurvey', response.data.registrationSurvey);
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                    ``
                });
        });
    },
};